import React, { useEffect, useState } from 'react'
import { Modal, message } from 'antd'
import dayjs from 'dayjs'
import api from '../../utils/api'
import { useAuth } from '../../hooks/AuthContext'
import ResponsiveSelect from '../common/ResponsiveSelect'
import { assignBookingStylist, getAssignError } from '../../api/booking'

/* ─────────── 指派人員 Modal（預約列表與待指派頁共用） ───────────
 *
 * 全部員工都要列出來，不能只列有班的人 —— 店家常常臨時請休假的人來支援，
 * 篩掉就沒得選了。所以分成「當天有值班／當日休假／未排班」三組，
 * 選擇權留給店家；只有「未開放此服務」的人才鎖住，因為那是店家自己設定的能力限制。
 *
 * 用 ResponsiveSelect 而不是 antd Select ——
 * B 端在平板／手機上用得很兇，浮層下拉在觸控裝置很難滑（見 CLAUDE.md）。
 */
const GROUP_LABELS = {
  shift: '當天有值班',
  off: '當日休假',
  unset: '未排班'
}

const describeShift = (e) => {
  if (e.status === 'shift') {
    const time =
      e.shift?.start && e.shift?.end ? ` ${e.shift.start}–${e.shift.end}` : ''
    return `（${e.shift?.label || '值班'}${time}）`
  }
  if (e.status === 'off') return `（${e.shift?.label || '休假'}）`
  return ''
}

export const buildAssignOptions = (roster) => {
  const groups = { shift: [], off: [], unset: [] }
  const blocked = []
  for (const e of roster || []) {
    if (e.canServe === false) {
      blocked.push({
        value: e._id,
        label: `${e.name}${describeShift(e)}・未開放此服務`,
        disabled: true
      })
      continue
    }
    ;(groups[e.status] || groups.unset).push({
      value: e._id,
      label: `${e.name}${describeShift(e)}`
    })
  }
  const out = []
  for (const key of ['shift', 'off', 'unset']) {
    if (groups[key].length) {
      out.push({
        label: `${GROUP_LABELS[key]}（${groups[key].length}）`,
        options: groups[key]
      })
    }
  }
  if (blocked.length) out.push({ label: '未開放此服務', options: blocked })
  return out
}

/** 預約帶到的服務 id（主服務 + services），給後端判斷誰能做 */
const serviceIdsOf = (booking) =>
  [booking?.productId, ...(booking?.services || []).map((s) => s?.serviceId)]
    .filter(Boolean)
    .map(String)

/**
 * @param {object}   booking     要指派的預約；null 時 Modal 關閉
 * @param {function} onClose     關閉（取消或指派完成後都會呼叫）
 * @param {function} onAssigned  指派成功後呼叫，讓外層重新載入清單
 */
export default function AssignStylistModal({ booking, onClose, onAssigned }) {
  const { businessId } = useAuth()
  const [stylistId, setStylistId] = useState(null)
  const [assigning, setAssigning] = useState(false)
  const [roster, setRoster] = useState({ loading: false, data: [], summary: null })

  // 打開就抓「那一天」的班表狀態
  useEffect(() => {
    setStylistId(null)
    if (!booking || !businessId) {
      setRoster({ loading: false, data: [], summary: null })
      return
    }
    let cancelled = false
    setRoster((r) => ({ ...r, loading: true }))
    ;(async () => {
      try {
        const serviceIds = serviceIdsOf(booking)
        const dateStr =
          typeof booking.date === 'string'
            ? booking.date.slice(0, 10)
            : dayjs(booking.date).format('YYYY-MM-DD')
        const { data } = await api.get(
          `/api/employees/${businessId}/schedule/${dateStr}`,
          { params: serviceIds.length ? { serviceIds: serviceIds.join(',') } : {} }
        )
        if (!cancelled) {
          setRoster({
            loading: false,
            data: Array.isArray(data?.data) ? data.data : [],
            summary: data?.summary || null
          })
        }
      } catch (e) {
        console.error('載入當天班表失敗:', e)
        // 班表查不到就退回全部員工，不能因為這個讓店家指派不了
        try {
          const { data } = await api.get(`/api/employees/${businessId}`)
          if (!cancelled) {
            setRoster({
              loading: false,
              data: (Array.isArray(data) ? data : []).map((emp) => ({
                _id: emp._id,
                name: emp.name,
                status: 'unset',
                shift: null,
                canServe: true
              })),
              summary: null
            })
          }
        } catch (e2) {
          if (!cancelled) setRoster({ loading: false, data: [], summary: null })
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [booking, businessId])

  const handleOk = async () => {
    if (!booking || !stylistId) return
    setAssigning(true)
    try {
      await assignBookingStylist(booking._id, stylistId)
      message.success('已指派人員')
      // Sidebar 的待指派紅點是 60 秒輪詢一次，不廣播的話指派完
      // 數字最多會停留一分鐘，看起來像沒清掉。事件名同時也用在 Sidebar.js。
      window.dispatchEvent(new Event('pending-assignment-changed'))
      onClose?.()
      onAssigned?.(booking)
    } catch (e) {
      message.error(getAssignError(e))
    } finally {
      setAssigning(false)
    }
  }

  return (
    <Modal
      title='指派人員'
      open={!!booking}
      onCancel={onClose}
      onOk={handleOk}
      okText='確認指派'
      cancelText='取消'
      okButtonProps={{ disabled: !stylistId, loading: assigning }}
      destroyOnClose
    >
      {booking && (
        <div>
          <div style={{ marginBottom: 12, lineHeight: 2 }}>
            <div>
              <strong>{booking.userName || '客人'}</strong>
              {booking.participantCount > 1 && `　${booking.participantCount} 人`}
            </div>
            <div style={{ color: 'rgba(0,0,0,0.6)' }}>
              {dayjs(booking.date).format('YYYY/MM/DD')} {booking.time}　
              {booking.productName}
            </div>
          </div>
          <ResponsiveSelect
            style={{ width: '100%' }}
            placeholder={roster.loading ? '載入當天班表中…' : '選擇要負責的人員'}
            value={stylistId}
            onChange={setStylistId}
            showSearch
            optionFilterProp='label'
            options={buildAssignOptions(roster.data)}
          />
          {roster.summary && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: 'rgba(0,0,0,0.6)',
                lineHeight: 1.8
              }}
            >
              {dayjs(booking.date).format('M/D')} 班表：值班{' '}
              <strong>{roster.summary.shift}</strong> 人・休假 {roster.summary.off}{' '}
              人・未排班 {roster.summary.unset} 人。休假的人員也可以指派，請先跟本人確認。
            </div>
          )}
          <div
            style={{
              marginTop: 10,
              fontSize: 12,
              color: 'rgba(0,0,0,0.45)',
              lineHeight: 1.8
            }}
          >
            指派時會再確認這位人員該時段有空。如果他已經被別的客人指名走了，
            系統會擋下來並告訴你。
          </div>
        </div>
      )}
    </Modal>
  )
}
